/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICookieContext } from '@msdyn365-commerce/core-internal/dist/types/interfaces/context/ICookieContext';
import { ProactiveChatNames } from '../../helpers/constants';
import { IChatContext, IPageVisitContext, TContextProvider } from '../../../interfaces';
import { IMsdyn365CsChatConnectorViewProps } from '../../msdyn365-cs-chat-connector';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * PageVisitTrigger class.
 * Proactive chat is triggered if user has visited current page for (config.proactiveOnPageVisitNumber) or more times.
 */
export default class PageVisitTrigger extends ProactiveChatTrigger {
    private visitCounter: number = 0;
    private cookieName: string = 'TimesPageVisited';
    private expdate: Date;
    private cookies: ICookieContext;

    /**
     * Class constructor.
     * @param {IMsdyn365CsChatConnectorViewProps} props The chat connector view properties.
     * @param {IChatContext} context The context.
     */
    constructor(props: IMsdyn365CsChatConnectorViewProps, context: IChatContext) {
        super(props, context);
        this.cookies = this.props.context.request.cookies;
        this.expdate = new Date();

        // Set expiry date to 365 days.
        this.expdate.setTime(this.expdate.getTime() + 24 * 60 * 60 * 1000 * 365);
        this.props.context.telemetry.debug(`[Chat][${PageVisitTrigger.name}] Cookie expiry date set to: '${this.expdate}'.`);

        this.props.context.telemetry.debug(`[Chat][${PageVisitTrigger.name}] Cookie context is given: '${this.cookies.isConsentGiven()}'.`);
        if (this.cookies.isConsentGiven()) {
            this.cookies.setConsentCookie();
        }

        this._updateVisitCounter();
    }

    /**
     * Validates whether the condition(s) is met to trigger proactive chat: current page count is great than or equal to threshold.
     * @returns {Promise<boolean>} True if condition is met. Otherwise, false.
     */
    public async validate(): Promise<boolean> {
        let retVal: boolean = false;

        if (this.props.config.proactiveOnPageVisitNumber) {
            retVal = this.visitCounter >= this.props.config.proactiveOnPageVisitNumber;
        }

        this.props.context.telemetry.debug(`[Chat][${PageVisitTrigger.name}] Validation result: '${retVal}'.`);
        return retVal;
    }

    /**
     * Resets the page visit counter cookie to zero.
     */
    public clear(): void {
        this.props.context.telemetry.debug(`[Chat][${PageVisitTrigger.name}] Clearing cookie for page visit counter.`);
        this.cookies.set<number>(this.cookieName, 0, {
            domain: window.location.hostname,
            path: window.location.pathname,
            expires: this.expdate
        });
    }

    /**
     * Gets the context.
     * @returns {TContextProvider} The context.
     */
    public getContextProvider(): TContextProvider {
        return (): IPageVisitContext => {
            return {
                ...this.context,
                IsProactiveChat: {
                    value: 'True',
                    isDisplayable: true
                },
                ProactiveType: {
                    value: ProactiveChatNames.PageVisits,
                    isDisplayable: true
                },
                NumberOfVisits: {
                    value: this.visitCounter,
                    isDisplayable: true
                }
            };
        };
    }

    /**
     * Gets the message to be displayed when proactive chat is triggered.
     * @returns {string} The message to be displayed when proactive chat is triggered.
     */
    public getMessage(): string {
        return this.props.config.proactiveOnPageVisitMessage || this.props.config.proactiveDefaultMessage;
    }

    /**
     * Increments the page visit counter by one.
     */
    private _updateVisitCounter(): void {
        this.visitCounter = this.cookies.get<number>(this.cookieName).value || 0;

        this.props.context.telemetry.debug(`[Chat][${PageVisitTrigger.name}] Page visit counter BEFORE update: '${this.visitCounter}.`);
        ++this.visitCounter;
        this.props.context.telemetry.debug(`[Chat][${PageVisitTrigger.name}] Page visit counter AFTER update: '${this.visitCounter}.`);

        this.cookies.set<number>(this.cookieName, this.visitCounter, {
            domain: window.location.hostname,
            path: window.location.pathname,
            expires: this.expdate
        });
    }
}
